import { addDocument, updateDocument, getDocument, getAllDocuments, queryDocuments } from './dbService';
import { auth } from '../config/firebase';
import { db } from '../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// Submit an APK for analysis
export const submitApkForAnalysis = async (fileId, fileName, downloadUrl) => {
  try {
    const user = auth.currentUser;
    
    if (!fileId) {
      throw new Error('fileId is required to submit analysis');
    }
    
    const analysisData = {
      fileId,
      fileName,
      downloadUrl,
      userId: user ? user.uid : 'anonymous',
      userEmail: user ? user.email : 'anonymous@user.com',
      submittedAt: new Date(),
      status: 'pending',
      progress: 0,
      results: null
    };
    
    const existingDoc = await getDocument('apkAnalysisUIProgress', fileId);
    if (existingDoc) {
      await updateDocument('apkAnalysisUIProgress', fileId, analysisData);
    } else {
      await updateDocument('apkAnalysisUIProgress', fileId, analysisData);
    }
    
    return { analysisId: fileId, status: 'pending' };
  } catch (error) {
    console.error('Error submitting APK for analysis:', error);
    throw error;
  }
};

/**
 * Triggers APK analysis by writing a request document to Firestore.
 * A Cloud Function (onAnalysisRequested) listens for new documents
 * in "apkAnalysisRequests" and runs the analysis server-side.
 *
 * This approach avoids the Cloud Run IAM issue with Gen 2 callable functions.
 */
export const triggerApkAnalysis = async (fileId) => {
  if (!fileId) {
    throw new Error('fileId is required');
  }

  const user = auth.currentUser;
  console.log(`[triggerApkAnalysis] === START === fileId: ${fileId}`);

  try {
    // Step 1: Create/update the progress document so the UI can track it
    const existingDoc = await getDocument('apkAnalysisUIProgress', fileId);
    if (!existingDoc) {
      console.log(`[triggerApkAnalysis] Creating initial progress doc for ${fileId}`);
      await updateDocument('apkAnalysisUIProgress', fileId, {
        fileId,
        userId: user ? user.uid : 'anonymous',
        userEmail: user ? user.email : 'anonymous@user.com',
        submittedAt: new Date(),
        status: 'pending',
        progress: 0,
        results: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    } else {
      console.log(`[triggerApkAnalysis] Progress doc exists for ${fileId}, status: ${existingDoc.status}`);
    }

    // Step 2: Write a request document to trigger the Cloud Function
    console.log(`[triggerApkAnalysis] Writing analysis request to Firestore...`);
    const requestId = await addDocument('apkAnalysisRequests', {
      fileId,
      userId: user ? user.uid : 'anonymous',
      userEmail: user ? user.email : 'anonymous@user.com',
      requestedAt: new Date(),
      status: 'pending',
    });

    console.log(`[triggerApkAnalysis] ✅ Request created: ${requestId}`);
    console.log(`[triggerApkAnalysis] Cloud Function will pick it up automatically via Firestore trigger`);

    // Step 3: Wait for analysis to start (listen for progress updates)
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe();
        console.warn(`[triggerApkAnalysis] Timeout waiting for analysis to start`);
        // Resolve anyway — the trigger may still be processing
        resolve({
          status: 'triggered',
          fileId,
          requestId,
          message: 'Analysis request submitted. It may take a moment to start.',
        });
      }, 30000); // 30s timeout

      const unsubscribe = onSnapshot(
        doc(db, 'apkAnalysisUIProgress', fileId),
        (snapshot) => {
          const data = snapshot.data();
          if (!data) return;

          // Once status moves beyond 'pending', the analysis has started
          if (data.status === 'processing' || data.status === 'completed') {
            console.log(`[triggerApkAnalysis] Analysis is ${data.status} (${data.progress}%)`);
            clearTimeout(timeout);
            unsubscribe();
            resolve({
              status: data.status,
              fileId,
              requestId,
              message: `Analysis ${data.status}`,
            });
          }

          if (data.status === 'error') {
            console.error(`[triggerApkAnalysis] Analysis errored:`, data.error);
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error || 'Analysis failed'));
          }
        },
        (error) => {
          console.error(`[triggerApkAnalysis] Snapshot error:`, error);
          clearTimeout(timeout);
          unsubscribe();
          // Resolve anyway — the request was submitted
          resolve({
            status: 'triggered',
            fileId,
            requestId,
            message: 'Analysis request submitted.',
          });
        }
      );
    });

  } catch (error) {
    console.error(`[triggerApkAnalysis] ❌ Error:`, error);
    throw error;
  }
};

// Get analysis status and results
export const getAnalysisResults = async (analysisId) => {
  try {
    return await getDocument('apkAnalysisUIProgress', analysisId);
  } catch (error) {
    console.error('Error getting analysis results:', error);
    throw error;
  }
};

// Get all analyses for current user
export const getUserAnalyses = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be logged in to fetch analyses');
    }
    const allAnalyses = await getAllDocuments('apkAnalysisUIProgress');
    return allAnalyses.filter(analysis => analysis.userId === user.uid);
  } catch (error) {
    console.error('Error getting user analyses:', error);
    throw error;
  }
};
